import React from 'react';
import {connect} from 'react-redux';

import {RegistrationTable} from 'notebook/registration-tables/RegistrationTableConstants';
import AddStageRunSamplesModal from 'notebook/sample-propagation/legacy/legacy-add-samples-modal/AddStageRunSamplesModal';
import TransferTableNonContainableEntityFilter from 'notebook/transfer-tables/TransferTableNonContainableEntityFilter';
import {BenchlingState} from 'redux-stores/reduceApp';
import useTagSchemaIdsQuery from 'schemas/useTagSchemaIdsQuery';
import LegacyDropdownMenuItem from 'ui-library/dropdowns/LegacyDropdownMenuConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import ModalAnchor from 'ui-library/ModalAnchor';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import {
  getAvailableSampleKeysByTableName,
  getTableIconsByTableName,
  resolveSampleKeysByTableName,
  SampleDetailsByTableName,
  SampleDetailsWithStageRunSample,
  SampleKeysByTableName,
} from 'workflows/util/StageRunUtil';
import {RegistrationRecordList, StageRun, StageRunSample} from 'workflows/WorkflowConstants';

function mapStateToProps(state: BenchlingState): {
  registrationRecordListsById: {[id: string]: RegistrationRecordList};
  stageRunSampleById: {[id: string]: StageRunSample};
} {
  return {
    registrationRecordListsById: state.models.registrationRecordListsById,
    stageRunSampleById: new WorkflowSelector(state.models.workflows).getStageRunSampleById(),
  };
}

interface BaseTransferTableStageRunSampleLinkContextProps {
  children: (args: {canAddSamples: boolean; addMenuItems: Array<LegacyDropdownMenuItem>}) => JSX.Element;
  stageRuns: Array<StageRun>;
  registrationTables: Array<RegistrationTable>;
  onAddSampleRows: (sampleDetails: Array<SampleDetailsWithStageRunSample>) => void;
}

interface ConnectedProps {
  // mapWorkflowStoreToProps
  stageRunSampleById: {[id: string]: StageRunSample};

  // mapStateToProps
  registrationRecordListsById: {[id: string]: RegistrationRecordList};
}

export interface TransferTableStageRunSampleLinkContextProps
  extends BaseTransferTableStageRunSampleLinkContextProps, ConnectedProps {
  tagSchemaIds: Set<string>;
}

interface TransferTableStageRunSampleLinkContextState {
  isModalOpen: boolean;
}

export class TransferTableStageRunSampleLinkContext extends React.PureComponent<
  TransferTableStageRunSampleLinkContextProps,
  TransferTableStageRunSampleLinkContextState
> {
  override state: TransferTableStageRunSampleLinkContextState = {isModalOpen: false};

  _handleOpenAddSamplesModal = (): void => {
    this.setState({isModalOpen: true});
  };

  _handleCloseModal = (): void => {
    this.setState({isModalOpen: false});
  };

  _handleAddSamples = (sampleDetails: Array<SampleDetailsWithStageRunSample>): void => {
    this.props.onAddSampleRows(sampleDetails);
  };

  _getAvailableStageRunSampleKeysByTableName(): SampleKeysByTableName {
    return getAvailableSampleKeysByTableName({
      stageRuns: this.props.stageRuns,
      registrationTables: this.props.registrationTables,
      registrationRecordListsById: this.props.registrationRecordListsById,
      tagSchemaIds: this.props.tagSchemaIds,
    });
  }

  _getTableIcons(): {[tableName: string]: BenchlingIconName} {
    return getTableIconsByTableName({registrationTables: this.props.registrationTables});
  }

  _canAddStageRunSamples(): boolean {
    const sampleKeys = this._getAvailableStageRunSampleKeysByTableName();
    for (const tableName of Object.keys(sampleKeys)) {
      if (sampleKeys[tableName].length > 0) {
        return true;
      }
    }
    return false;
  }

  _renderAddSamplesModalForSamples(sampleDetailsByTableName: SampleDetailsByTableName): JSX.Element {
    return (
      <AddStageRunSamplesModal
        enableRowCounts={true}
        title="Add rows to transfer table"
        dropdownIconByTableName={this._getTableIcons()}
        stageRunSampleDetailsByTableName={sampleDetailsByTableName}
        onStageRunSamplesSelected={this._handleAddSamples}
        onRequestClose={this._handleCloseModal}
      />
    );
  }

  _renderAddSamplesModal(): JSX.Element {
    const availableSampleKeys = this._getAvailableStageRunSampleKeysByTableName();
    const sampleDetailsByTableName = resolveSampleKeysByTableName(
      availableSampleKeys,
      this.props.stageRuns,
      this.props.stageRunSampleById
    );

    return (
      <ModalAnchor open={true}>
        <TransferTableNonContainableEntityFilter sampleDetailsByTableName={sampleDetailsByTableName}>
          {(filteredSampleDetailsByTableName: SampleDetailsByTableName, isLoading: boolean) =>
            isLoading
              ? this._renderAddSamplesModalForSamples({})
              : this._renderAddSamplesModalForSamples(filteredSampleDetailsByTableName)
          }
        </TransferTableNonContainableEntityFilter>
      </ModalAnchor>
    );
  }

  override render(): JSX.Element {
    return (
      <>
        {this.state.isModalOpen && this._renderAddSamplesModal()}
        {this.props.children({
          canAddSamples: this._canAddStageRunSamples(),
          addMenuItems: [
            {
              name: 'Add samples',
              icon: 'compose',
              handleOnClick: this._handleOpenAddSamplesModal,
            },
          ],
        })}
      </>
    );
  }
}

function QueryingTransferTableStageRunSampleLinkContext(
  props: BaseTransferTableStageRunSampleLinkContextProps & ConnectedProps
): JSX.Element {
  const tagSchemaIds = useTagSchemaIdsQuery();
  return <TransferTableStageRunSampleLinkContext {...props} tagSchemaIds={tagSchemaIds ?? new Set()} />;
}

export default connect(mapStateToProps)(QueryingTransferTableStageRunSampleLinkContext);
